<script setup>
import Progressbar from "@/components/Tools/Progressbar.vue";
import store from "@/core/services/store";
import {
  UPDATE_PERSONAL_INFO,
  UPDATE_USER_DATA
} from "@/core/services/store/profile.module";
import JwtService from "@/core/services/jwt.service";
import ApiService from "@/core/services/api.service";
import {
  UPDATE_SELECTED_PRESET_VERSION,
  UPDATE_SELECTED_PROJECT
} from "@/core/services/store/switchArea.module";
import i18nService from "@/core/services/i18n.service";
import { SET_INSTANCE } from "@/core/services/store/instance.module";
import Auth from "@/components/General/Auth/auth";
import { onBeforeUnmount, ref } from "vue";
import { getExpectedOrigin } from "@/components/IFrame/iframehelper";
import { useTheme } from "@/composables/useTheme";

const isLoading = ref(true);

async function isValidAuthMessage(data) {
  if (!data || typeof data !== "object") return false;
  if (data.type === "reload") {
    await updateLanguage(data.language);
    location.reload();
  }
  if (data.type !== "auth") return false;
  if (!data.token || typeof data.token !== "string") return false;

  // basic JWT structure validation
  const parts = data.token.split(".");
  if (parts.length !== 3) return false;

  if (data.projectId && typeof data.projectId !== "string") {
    return false;
  }

  if (
    data.language &&
    (typeof data.language !== "string" || data.language.length > 10)
  ) {
    return false;
  }

  return true;
}

let lastMessageTime = 0;
const MESSAGE_THROTTLE_MS = 500;

function shouldThrottleMessage() {
  const now = Date.now();
  if (now - lastMessageTime < MESSAGE_THROTTLE_MS) {
    return true;
  }
  lastMessageTime = now;
  return false;
}

function handleMessage(event) {
  if (shouldThrottleMessage()) {
    return;
  }

  const eSource = event.source;
  const parent = window.parent;

  const expectedOrigin = getExpectedOrigin();
  if (
    !expectedOrigin ||
    event.origin !== expectedOrigin ||
    eSource !== parent
  ) {
    return;
  }

  const data = event.data;

  // Handle standalone theme messages
  if (data?.type === "theme" && data?.themeMode) {
    const { applyTheme } = useTheme();
    applyTheme(data.themeMode);
    return;
  }

  // Validate message structure and content
  if (!isValidAuthMessage(data)) {
    return;
  }

  updateData(data);
}

async function updateData(data) {
  await store.dispatch(UPDATE_PERSONAL_INFO, null);
  await store.dispatch(UPDATE_USER_DATA, null);

  // Save the new token and set API headers
  JwtService.saveToken(data.token);
  ApiService.setHeader();

  // Fetch user and instance data
  await getUser();
  if (!store.getters["instance/instance"]?.id) {
    await getInstance();
  }

  // Update other parts of the store with sanitized data
  if (data.projectId) {
    await store.dispatch(UPDATE_SELECTED_PROJECT, {
      id: String(data.projectId)
    });
  }

  if (data.selectedPresetVersionId && data.isDevVersion) {
    await store.dispatch(UPDATE_SELECTED_PRESET_VERSION, {
      id: String(data.selectedPresetVersionId),
      is_dev_version: data.isDevVersion
    });
  }

  if (data.language) {
    await updateLanguage(data.language);
  }

  if (data.themeMode) {
    const { applyTheme } = useTheme();
    applyTheme(data.themeMode);
  }

  isLoading.value = false;
}

async function updateLanguage(language) {
  // Sanitize language code - only allow alphanumeric and hyphens
  const sanitizedLanguage = language
    .replace(/[^a-zA-Z0-9-]/g, "")
    .substring(0, 10);
  if (sanitizedLanguage) {
    await i18nService.setActiveLanguage(sanitizedLanguage);
  }
}

async function getInstance() {
  const url = process.env.VUE_APP_API_ADMIN + "/instance";
  const response = await ApiService.get(url);
  await store.dispatch("instance/" + SET_INSTANCE, response.data);
}

async function getUser() {
  const url = process.env.VUE_APP_API_ADMIN + "/auth/me";
  const response = await ApiService.get(url);
  if (response.data) {
    Auth.updateUserData(response.data);
  }
}

window.addEventListener("message", handleMessage);

onBeforeUnmount(() => {
  window.removeEventListener("message", handleMessage);
});
</script>

<template>
  <div class="d-flex h-100">
    <div class="flex-column flex-grow-0 border-right connect-sidebar"></div>
    <div
      class="flex-column flex-row flex-grow-1 main-panel-wrapper overflow-hidden"
    >
      <div class="main-panel position-relative">
        <Progressbar />
        <div v-if="!isLoading" class="py-4 px-6 h-100">
          <router-view />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.grid {
  display: grid;
  grid-template-rows: auto 1fr auto;
  min-height: 100vh;
}
.main-panel {
  background-color: var(--ax-bg-body, #{$color-connect-background});
  min-height: 100%;
  height: 100%;
}
.connect-sidebar {
  height: 100vh;
  position: sticky;
  top: 0;
}
</style>
